// The app plan: what the wizard shows, what the user edits, and what the generator is
// finally told to build. One document, edited in place — see
// docs/superpowers/specs/2026-09-08-guided-front-door-design.md.

import { KIT_IDS } from '@/webapp/runtime/kits';

/** Exactly the primitives the runtime stylesheet can draw (`.s-ring/.s-bars/.s-heat/.s-meter`).
 *  A suggestion outside this set is a promise the build has to break. */
export const CHART_KINDS = ['ring', 'bars', 'heat', 'meter'] as const;
export type ChartKind = (typeof CHART_KINDS)[number];

export type AppType = 'catalog' | 'rsvp' | 'tracker' | 'request' | 'general';

export interface PlanFeature {
  /** Slug of the label — stable across edits, so toggling never reorders or duplicates. */
  id: string;
  label: string;
  /** One line the generator can act on. */
  detail: string;
  included: boolean;
  origin: 'scaffold' | 'model' | 'user';
}

export interface PlanChart {
  id: string;
  label: string;
  kind: ChartKind;
  /** What the chart is drawn from, in the app's own terms. */
  source: string;
  included: boolean;
}

export interface PlanScreen {
  id: string;
  label: string;
  purpose: string;
}

/**
 * A collection the OWNER maintains and visitors read — a rota, a class timetable, this
 * week's sessions.
 *
 * The counterpart to `collects`, and just as load-bearing: it is the difference between a
 * rota the manager posts on Monday night and a rota baked into the page that needs
 * rebuilding to change. Left to prose, a generated rota used `S.store` — a per-browser
 * seed — and the one thing that app exists to do could not be done.
 *
 * The field names matter because the owner types these rows in by hand. A field the app
 * renamed is a column they cannot know to fill.
 */
export interface PlanManaged {
  /** The `S.list` collection name. */
  collection: string;
  /** What each row holds, e.g. ['name', 'startsAt', 'places']. */
  fields: string[];
}

/** Set when the app collects from visitors via `S.submit`. */
export interface PlanCollects {
  /** The S.submit collection name; `submitEntities` derives the server schema from it. */
  collection: string;
  /** What each submission holds, e.g. ['name', 'attending', 'guests']. */
  fields: string[];
}

/**
 * Something the subject obliges the app to handle, surfaced BEFORE it is built — and,
 * more usefully, the creative way around it.
 *
 * Some apps carry obligations the person asking will not think to mention: a scripture
 * reader may only ship public-domain translations and should not draw a prophet; a
 * training app should not programme through pain; a condition report is one party's
 * evidence, not a verdict. Found after the build these mean rework; found at plan time
 * they are just requirements.
 *
 * The important half is `workaround`. A constraint that only forbids produces a worse
 * app — "no image here" leaves a hole. A constraint that SUBSTITUTES produces a better
 * one: the sun rising over the horizon instead of a figure, geometry instead of a face.
 * So each carries the alternative, and the user can rewrite it in their own words on the
 * review screen: "don't draw God, show the sun". That is the whole point of showing
 * these before the build rather than enforcing them silently after it.
 */
export interface PlanConstraint {
  id: string;
  /** The rule, in one short line. */
  label: string;
  /** Why it applies. */
  detail: string;
  /** The creative substitution — what to do INSTEAD. Editable by the user. */
  workaround: string;
  kind: ConstraintKind;
}

export const CONSTRAINT_KINDS = ['legal', 'safety', 'privacy', 'accuracy', 'cost'] as const;
export type ConstraintKind = (typeof CONSTRAINT_KINDS)[number];

/**
 * Rules the SERVER enforces on the collection this app collects into.
 *
 * The page cannot be trusted with any of these — anyone holding the link can edit it — so
 * they are decided at plan time, declared at publish, and enforced in `crud.ts`. The
 * enforcement has existed and been tested since the appRules work; this is what finally
 * switches it on for a real app.
 */
export interface PlanRules {
  /** Places per group, e.g. 20 seats per night. `groupBy` names the field that buckets it. */
  capacity?: { groupBy?: string; limit: number };
  /** Submissions refused outside this window, ISO dates. A real deadline, not a suggestion. */
  window?: { opensAt?: string; closesAt?: string };
  /** One row per distinct value — no double-RSVP, one plot per member. */
  uniqueBy?: string;
  /** At capacity: refuse, or accept and mark the row waitlisted. */
  overflow?: 'reject' | 'waitlist';
  /** Publish a PUBLIC aggregate grouped by this field, so the page can say "4 left"
   *  without any visitor being able to read who the other bookings belong to. */
  countBy?: string;
}

export interface AppPlan {
  type: AppType;
  appName: string;
  tagline: string;
  kitId: string;
  features: PlanFeature[];
  screens: PlanScreen[];
  charts: PlanChart[];
  collects: PlanCollects | null;
  /** What the seeded sample content should be. Empty for a collecting app. */
  seedNote: string;
  /** Obligations the subject carries. Shown to the user, sent to the generator. */
  constraints: PlanConstraint[];
  /** Server-enforced rules for the collecting half. Null when the app collects nothing. */
  rules: PlanRules | null;
  /** Collections the owner maintains after publishing, which visitors read. */
  manages: PlanManaged[];
}

const MAX_FEATURES = 10;
const MAX_SCREENS = 5;
const MAX_CHARTS = 3;
const MAX_CONSTRAINTS = 5;

export function isCollecting(plan: AppPlan): boolean {
  return plan.collects != null;
}

export function slugify(s: string): string {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Slug, made unique against ids already taken. */
function uniqueId(label: string, taken: Set<string>, fallback: string): string {
  const base = slugify(label) || fallback;
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  taken.add(id);
  return id;
}

const str = (v: unknown, dflt = ''): string => (typeof v === 'string' && v.trim() ? v.trim() : dflt);
const bool = (v: unknown, dflt: boolean): boolean => (typeof v === 'boolean' ? v : dflt);

/** Pull the JSON object out of a reply that may be fenced or wrapped in prose. */
function parseLoose(raw: unknown): Record<string, unknown> | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw !== 'string') return null;
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[0]);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function normalizeFeatures(raw: unknown, fallback: PlanFeature[]): PlanFeature[] {
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const taken = new Set<string>();
  const out: PlanFeature[] = [];
  raw.slice(0, MAX_FEATURES).forEach((item, i) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const label = str(r.label);
    if (!label) return;
    out.push({
      id: uniqueId(label, taken, `feature-${i + 1}`),
      label,
      detail: str(r.detail, label),
      included: bool(r.included, true),
      origin: 'model',
    });
  });
  return out.length ? out : fallback;
}

function normalizeScreens(raw: unknown, fallback: PlanScreen[]): PlanScreen[] {
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const taken = new Set<string>();
  const out: PlanScreen[] = [];
  raw.slice(0, MAX_SCREENS).forEach((item, i) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const label = str(r.label);
    if (!label) return;
    out.push({ id: uniqueId(label, taken, `screen-${i + 1}`), label, purpose: str(r.purpose, label) });
  });
  return out.length ? out : fallback;
}

function normalizeCharts(raw: unknown, fallback: PlanChart[]): PlanChart[] {
  if (!Array.isArray(raw)) return fallback;
  const taken = new Set<string>();
  const out: PlanChart[] = [];
  raw.slice(0, MAX_CHARTS).forEach((item, i) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const label = str(r.label);
    const kind = r.kind as ChartKind;
    if (!label || !CHART_KINDS.includes(kind)) return;
    out.push({
      id: uniqueId(label, taken, `chart-${i + 1}`),
      label,
      kind,
      source: str(r.source, label),
      included: bool(r.included, true),
    });
  });
  return out;
}


/**
 * Server rules, kept only where they would actually hold.
 *
 * Two things are dropped rather than published, because both produce an app that looks
 * configured and behaves wrongly, with nothing on screen saying why:
 *
 *  - A DEADLINE ALREADY PAST. The model does not know today's date and has been seen
 *    setting one a year gone; the server then refuses every submission from the moment the
 *    app is published. A deadline that has passed is not a deadline, it is a closed app.
 *  - A FIELD THE COLLECTION DOES NOT HAVE. `uniqueBy: "guestCode"` against a form that
 *    collects name and email silently does nothing — the rule reads as protection and is
 *    not there.
 */
function normalizeRules(
  raw: unknown,
  collecting: boolean,
  fields: string[] = [],
  now: Date = new Date(),
): PlanRules | null {
  if (!collecting) return null;
  const r = (raw ?? {}) as Record<string, unknown>;
  const out: PlanRules = {};

  // A rule can only key on something the visitor actually sends. With no declared fields
  // there is nothing to check against, so everything is allowed through.
  const known = (name: string) => !!name && (fields.length === 0 || fields.includes(name));

  const cap = (r.capacity ?? {}) as Record<string, unknown>;
  const limit = Number(cap.limit);
  // A capacity of zero or a fraction is not a limit anyone meant — drop it rather than
  // publishing a rule that refuses every submission.
  if (Number.isFinite(limit) && limit >= 1) {
    const groupBy = str(cap.groupBy);
    out.capacity = { limit: Math.floor(limit), ...(known(groupBy) ? { groupBy } : {}) };
  }

  const win = (r.window ?? {}) as Record<string, unknown>;
  const opensAt = str(win.opensAt);
  const closesAt = str(win.closesAt);
  // Only real dates: an unparseable one would otherwise lock every visitor out.
  const usable = (v: string) => !!v && !Number.isNaN(Date.parse(v));
  const stillOpen = (v: string) => usable(v) && Date.parse(v) > now.getTime();
  if (usable(opensAt) || stillOpen(closesAt)) {
    out.window = {
      ...(usable(opensAt) ? { opensAt } : {}),
      ...(stillOpen(closesAt) ? { closesAt } : {}),
    };
  }

  if (known(str(r.uniqueBy))) out.uniqueBy = str(r.uniqueBy);
  if (r.overflow === 'waitlist' || r.overflow === 'reject') out.overflow = r.overflow;
  if (known(str(r.countBy))) out.countBy = str(r.countBy);

  return Object.keys(out).length ? out : null;
}

function normalizeConstraints(raw: unknown, fallback: PlanConstraint[]): PlanConstraint[] {
  if (!Array.isArray(raw)) return fallback;
  const taken = new Set<string>();
  const out: PlanConstraint[] = [];
  raw.slice(0, MAX_CONSTRAINTS).forEach((item, i) => {
    const r = (item ?? {}) as Record<string, unknown>;
    const label = str(r.label);
    if (!label) return;
    const kind = CONSTRAINT_KINDS.includes(r.kind as ConstraintKind) ? (r.kind as ConstraintKind) : 'accuracy';
    out.push({
      id: uniqueId(label, taken, `constraint-${i + 1}`),
      label,
      detail: str(r.detail, label),
      workaround: str(r.workaround),
      kind,
    });
  });
  return out.length ? out : fallback;
}

/** At most two: a parent and its children (a class and its sessions) is the deepest shape
 *  any of these apps needs, and a third is the model inventing structure. */
const MAX_MANAGED = 2;

function normalizeManaged(
  raw: unknown,
  fallback: PlanManaged[],
  collects?: string,
): PlanManaged[] {
  if (!Array.isArray(raw)) return fallback;
  const out: PlanManaged[] = [];
  // The collecting collection can never be owner-managed: it is declared owner-write, and
  // the visitors it exists for would be refused.
  const taken = new Set<string>(collects ? [collects] : []);
  for (const item of raw.slice(0, MAX_MANAGED)) {
    const r = (item ?? {}) as Record<string, unknown>;
    // Becomes a URL path segment and a server entity name, so it must start with a letter.
    const collection = str(r.collection).replace(/[^A-Za-z0-9_]/g, '');
    if (!/^[A-Za-z]/.test(collection) || taken.has(collection)) continue;
    const fields = Array.isArray(r.fields)
      ? r.fields.filter((f): f is string => typeof f === 'string' && !!f.trim()).map((f) => f.trim()).slice(0, 8)
      : [];
    if (!fields.length) continue; // a collection with no columns is one nobody can fill in
    taken.add(collection);
    out.push({ collection, fields });
  }
  return out.length ? out : fallback;
}

function normalizeCollects(raw: unknown, fallback: PlanCollects): PlanCollects {
  const r = (raw ?? {}) as Record<string, unknown>;
  const collection = str(r.collection).replace(/[^A-Za-z0-9_]/g, '');
  const fields = Array.isArray(r.fields)
    ? r.fields.filter((f): f is string => typeof f === 'string' && !!f.trim()).map((f) => f.trim()).slice(0, 10)
    : [];
  return {
    // Must start with a letter: it becomes a URL path segment and a server entity name.
    collection: /^[A-Za-z]/.test(collection) ? collection : fallback.collection,
    fields: fields.length ? fields : fallback.fields,
  };
}

/**
 * Merge a model reply onto the scaffold plan.
 *
 * Deliberately NOT a parser that can fail: the scaffold is the floor, so a null reply,
 * prose, or a half-filled object all yield a complete, buildable plan. That is what lets
 * the plan screen paint before the model answers, and what keeps a failed planning call
 * from ever being a blank screen.
 */
export function normalizePlan(raw: unknown, fallback: AppPlan): AppPlan {
  const r = parseLoose(raw);
  if (!r) return fallback;

  const kitId = typeof r.kitId === 'string' && KIT_IDS.includes(r.kitId) ? r.kitId : fallback.kitId;

  // The collection is write-only — S.submit cannot read it back — so a chart of
  // responses could only be drawn from invented numbers, which the build prompt
  // explicitly forbids on a page shown to strangers. The scaffold decides whether an app
  // collects; the model may not add charts to one that does.
  const collecting = fallback.collects != null;

  // Resolved before the rules, which key on these field names.
  const collects = collecting ? normalizeCollects(r.collects, fallback.collects!) : { collection: '', fields: [] };

  return {
    type: fallback.type,
    appName: str(r.appName, fallback.appName),
    tagline: str(r.tagline, fallback.tagline),
    kitId,
    features: normalizeFeatures(r.features, fallback.features),
    screens: normalizeScreens(r.screens, fallback.screens),
    charts: collecting ? [] : normalizeCharts(r.charts, fallback.charts),
    // WHETHER an app collects is the scaffold's decision — it determines the whole shape
    // and the model must not flip it. WHAT it collects is the idea's: a supper club needs
    // party size and which night, not the generic RSVP fields.
    collects: collecting ? collects : null,
    seedNote: collecting ? '' : str(r.seedNote, fallback.seedNote),
    constraints: normalizeConstraints(r.constraints, fallback.constraints),
    rules: normalizeRules(r.rules, collecting, collecting ? collects.fields : []) ?? fallback.rules,
    manages: normalizeManaged(r.manages, fallback.manages ?? [], collecting ? collects.collection : undefined),
  };
}

/** A user-added feature. Included by definition — they typed it because they want it. */
export function userFeature(label: string, existing: PlanFeature[]): PlanFeature {
  const taken = new Set(existing.map((f) => f.id));
  return {
    id: uniqueId(label, taken, `feature-${existing.length + 1}`),
    label: label.trim(),
    detail: label.trim(),
    included: true,
    origin: 'user',
  };
}
